import React, { useRef, useEffect, useState } from 'react';
import { useUserInteraction } from '../context/UserInteractionContext';
import './InitialMessageVideo.css';

interface InitialMessageVideoProps {
  src: string;
  autoplay?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const InitialMessageVideo: React.FC<InitialMessageVideoProps> = ({
  src,
  autoplay = true,
  className = '',
  style = {}
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { hasUserInteracted } = useUserInteraction();

  // Effect to handle autoplay when user interaction is detected
  useEffect(() => {
    const video = videoRef.current;
    if (!video || isPlaying || isLoading || hasError) return;

    if (hasUserInteracted && autoplay && video.readyState >= 3) {
      console.log('User interaction detected, attempting autoplay');
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Video autoplay with sound successful after user interaction');
            setIsPlaying(true);
          })
          .catch((error) => {
            console.log('Autoplay with sound failed, trying muted autoplay:', error);
            video.muted = true;
            return video.play();
          })
          .then(() => {
            if (video.muted) {
              console.log('Video autoplay muted successful after user interaction');
              setIsPlaying(true);
            }
          })
          .catch((error) => {
            console.error('All autoplay attempts failed after user interaction:', error);
          });
      }
    }
  }, [hasUserInteracted, autoplay, isPlaying, isLoading, hasError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      setIsLoading(false);
      if (autoplay && hasUserInteracted) {
        // Try to play with sound first (only after user interaction)
        const playPromise = video.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Video autoplay with sound successful');
              setIsPlaying(true);
            })
            .catch((error) => {
              console.log('Autoplay with sound failed, trying muted autoplay:', error);
              // If autoplay with sound fails, try muted autoplay
              video.muted = true;
              return video.play();
            })
            .then(() => {
              if (video.muted) {
                console.log('Video autoplay muted successful');
                setIsPlaying(true);
                // Show unmute button or instruction
              }
            })
            .catch((error) => {
              console.error('All autoplay attempts failed:', error);
              setIsPlaying(false);
            });
        }
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleError = (error: Event) => {
      console.error('Video error:', error);
      setHasError(true);
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    // Add event listeners
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('error', handleError);
    video.addEventListener('loadstart', handleLoadStart);

    // Cleanup
    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadstart', handleLoadStart);
    };
  }, [autoplay]);

  const handleUnmute = () => {
    const video = videoRef.current;
    if (video && video.muted) {
      video.muted = false;
      // Restart the video to play with sound
      video.currentTime = 0;
      video.play().catch(console.error);
    }
  };

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play().catch(console.error);
      } else {
        video.pause();
      }
    }
  };

  if (hasError) {
    return (
      <div className={`initial-video-error ${className}`} style={style}>
        <div className="error-content">
          <p>ไม่สามารถโหลดวิดีโอได้</p>
          <p>Unable to load video</p>
          <a href={src} target="_blank" rel="noopener noreferrer">
            เปิดวิดีโอในแท็บใหม่ / Open video in new tab
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`initial-video-container ${className}`} style={style}>
      {isLoading && (
        <div className="video-loading">
          <div className="loading-spinner"></div>
          <p>กำลังโหลดวิดีโอ...</p>
        </div>
      )}
      
      <video
        ref={videoRef}
        className="initial-video"
        controls
        playsInline
        preload="auto"
        loop={false}
        style={{ display: isLoading ? 'none' : 'block' }}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Unmute button overlay for muted autoplay */}
      {isPlaying && videoRef.current?.muted && (
        <div className="unmute-overlay">
          <button 
            className="unmute-button"
            onClick={handleUnmute}
            aria-label="เปิดเสียง / Unmute video"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
            <span>เปิดเสียง</span>
          </button>
        </div>
      )}

      {/* Play button overlay for paused video */}
      {!isPlaying && !isLoading && (
        <div className="play-overlay" onClick={handlePlayClick}>
          <button 
            className="play-button"
            aria-label="เล่นวิดีโอ / Play video"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};